/**
 * BoardgameDetailModal — shows boardgame details with per-user editing.
 * Uses inline field editing: each field is always visible and tappable.
 * Toggles and ratings are always interactive (no edit state needed).
 * Dates/selects/numbers/text enter edit mode on tap, save on blur/change.
 * Users are loaded dynamically from the UsersProvider context.
 */

import { useState, useEffect, useCallback } from 'react';
import { DetailModal, formatDate, formatDateShort, Pill } from '@code-burrow/design-system';
import { useUsers, deriveUserLabels, useUserView, useAddToList, usePod, PodMemberPicker, useCurrentUser } from '@code-burrow/tracker-core';

const BOARDGAME_USER_FIELDS = [
    { key: 'firstPlayed', label: 'First Played', type: 'date' },
    { key: 'rating', label: 'Rating', type: 'rating' },
    { key: 'owned', label: 'Owned', type: 'toggle' },
    { key: 'notes', label: 'Notes', type: 'text' },
];

function getUserField(item, userName, fieldName) {
    return item._userData?.[userName]?.[fieldName] ?? null;
}

/** Tag input with autocomplete suggestions from previously used names */
function TagInput({ value = [], onChange, suggestions = [] }) {
    const [input, setInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);

    const filtered = suggestions.filter(
        (s) => !value.includes(s) && s.toLowerCase().includes(input.toLowerCase())
    );

    const addTag = (tag) => {
        const trimmed = tag.trim();
        if (trimmed && !value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setInput('');
        setShowSuggestions(false);
    };

    const removeTag = (tag) => onChange(value.filter((t) => t !== tag));

    return (
        <div className="space-y-1.5">
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((tag) => (
                        <Pill key={tag} className="bg-accent-primary/15 text-cyan-300 gap-0.5">
                            {tag}
                            <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-400 transition-colors">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </Pill>
                    ))}
                </div>
            )}
            <div className="relative">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); if (input.trim()) addTag(input); }
                        if (e.key === 'Backspace' && !input && value.length > 0) removeTag(value[value.length - 1]);
                    }}
                    placeholder="Add name..."
                    className="w-full bg-bg-tertiary text-text-primary rounded px-2 py-1 text-xs border border-border-subtle"
                />
                {showSuggestions && filtered.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-bg-secondary border border-border-subtle rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {filtered.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => addTag(s)}
                                className="w-full text-left px-2 py-1 text-xs text-text-primary hover:bg-surface-hover transition-colors"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * PlayedWithRow — combines pod member toggle chips with free-text name input.
 * Label on the left, all chips/tags/input right-aligned.
 */
function PlayedWithRow({ tags = [], onTagsChange, suggestions = [], podMemberPicker, disabled }) {
    return (
        <div className="flex justify-between items-start min-h-[24px] gap-4">
            <span className="text-xs text-text-tertiary pt-1 shrink-0">Played With</span>
            <div className="flex flex-col items-end gap-1.5 min-w-0">
                {/* Pod member chips */}
                {podMemberPicker}
                {/* Free-text tag chips */}
                {tags.length > 0 && (
                    <div className="flex flex-wrap justify-end gap-1">
                        {tags.map((tag) => (
                            <Pill key={tag} className="bg-accent-primary/15 text-cyan-300 gap-0.5">
                                {tag}
                                <button type="button" onClick={() => {
                                    const next = tags.filter((t) => t !== tag);
                                    onTagsChange(next.length > 0 ? next : null);
                                }} className="hover:text-red-400 transition-colors">
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </Pill>
                        ))}
                    </div>
                )}
                {/* Compact add input */}
                <CompactTagInput
                    tags={tags}
                    onChange={onTagsChange}
                    suggestions={suggestions}
                    disabled={disabled}
                />
            </div>
        </div>
    );
}

/** Compact inline tag input — just the text field, no tag display (handled by parent) */
function CompactTagInput({ tags = [], onChange, suggestions = [], disabled }) {
    const [input, setInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);

    const filtered = suggestions.filter(
        (s) => !tags.includes(s) && s.toLowerCase().includes(input.toLowerCase())
    );

    const addTag = (tag) => {
        const trimmed = tag.trim();
        if (trimmed && !tags.includes(trimmed)) {
            onChange([...tags, trimmed]);
        }
        setInput('');
        setShowSuggestions(false);
    };

    return (
        <div className="relative">
            <input
                type="text"
                value={input}
                onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); if (input.trim()) addTag(input); }
                }}
                disabled={disabled}
                placeholder="Add name..."
                className="bg-bg-tertiary text-text-primary rounded px-2 py-1 text-xs border border-border-subtle w-[120px]"
            />
            {showSuggestions && filtered.length > 0 && (
                <div className="absolute z-20 right-0 mt-1 bg-bg-secondary border border-border-subtle rounded-md shadow-lg max-h-32 overflow-y-auto w-[150px]">
                    {filtered.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => addTag(s)}
                            className="w-full text-left px-2 py-1 text-xs text-text-primary hover:bg-surface-hover transition-colors"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * InlineField — a single field that shows its value and becomes editable on tap.
 * Toggles and ratings are always interactive (no edit state).
 * Dates open an input on tap, save on blur/change.
 * Text (notes) opens a textarea on tap, save on blur.
 * Tags (playedWith) are always shown with their own TagInput UI.
 */
function InlineField({ field, value, onSave, disabled, nameSuggestions }) {
    const [editing, setEditing] = useState(false);
    const [localVal, setLocalVal] = useState('');

    const startEdit = () => {
        if (disabled) return;
        if (field.type === 'date') {
            setLocalVal(value ? new Date(value).toISOString().split('T')[0] : '');
        } else if (field.type === 'text') {
            setLocalVal(value || '');
        }
        setEditing(true);
    };

    const commitDate = (val) => {
        const parsed = val ? new Date(val).toISOString() : null;
        if (parsed !== (value ? new Date(value).toISOString() : null)) {
            onSave(field.key, parsed);
        }
        setEditing(false);
    };

    const commitText = (val) => {
        const cleaned = val.trim() || null;
        if (cleaned !== (value || null)) {
            onSave(field.key, cleaned);
        }
        setEditing(false);
    };

    // -- Toggle: always interactive, no edit state --
    if (field.type === 'toggle') {
        const isOn = !!value;
        return (
            <div className="flex justify-between items-center min-h-[24px]">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={isOn}
                    disabled={disabled}
                    onClick={() => onSave(field.key, !isOn)}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors duration-200 ${
                        isOn ? 'bg-accent-primary/60' : 'bg-bg-tertiary border border-border-subtle'
                    } ${disabled ? 'opacity-50' : ''}`}
                >
                    <span className={`inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        isOn ? 'translate-x-[14px]' : 'translate-x-0.5'
                    }`} />
                </button>
            </div>
        );
    }

    // -- Rating: always interactive, tap stars --
    if (field.type === 'rating') {
        const rating = value || 0;
        return (
            <div className="flex justify-between items-center min-h-[24px]">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            disabled={disabled}
                            onClick={() => onSave(field.key, rating === n ? null : n)}
                            className={`text-base leading-none transition-colors ${
                                n <= rating ? 'text-yellow-400' : 'text-text-tertiary/30 hover:text-text-tertiary/60'
                            } ${disabled ? 'opacity-50' : ''}`}
                        >&#9733;</button>
                    ))}
                </div>
            </div>
        );
    }

    // -- Date: tap to open picker --
    if (field.type === 'date') {
        if (editing) {
            return (
                <div className="flex justify-between items-center min-h-[24px]">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <div className="flex items-center gap-1">
                        <input
                            type="date"
                            value={localVal}
                            onChange={(e) => setLocalVal(e.target.value)}
                            onBlur={() => commitDate(localVal)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitDate(localVal); if (e.key === 'Escape') setEditing(false); }}
                            autoFocus
                            className="h-[22px] px-2 rounded-sm bg-bg-primary border border-border text-text-primary text-xs focus:outline-none w-[130px] [color-scheme:dark]"
                        />
                        {localVal && (
                            <button
                                type="button"
                                onClick={() => { setLocalVal(''); commitDate(''); }}
                                className="text-text-tertiary hover:text-text-secondary"
                                title="Clear"
                            >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary">{value ? formatDate(value) : '\u2014'}</span>
            </div>
        );
    }

    // -- Text (notes): tap to edit, save on blur --
    if (field.type === 'text') {
        if (editing) {
            return (
                <div className="space-y-1">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <textarea
                        value={localVal}
                        onChange={(e) => setLocalVal(e.target.value)}
                        onBlur={() => commitText(localVal)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
                        autoFocus
                        rows={2}
                        className="w-full bg-bg-tertiary text-text-primary rounded px-2 py-1 text-xs border border-border-subtle"
                    />
                </div>
            );
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary truncate max-w-[60%] text-right">{value || '\u2014'}</span>
            </div>
        );
    }

    // -- Tags (playedWith): always shown with TagInput, saves on change --
    if (field.type === 'tags') {
        const tags = Array.isArray(value) ? value : [];
        return (
            <div className="space-y-1">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <TagInput
                    value={tags}
                    onChange={(newTags) => onSave(field.key, newTags.length > 0 ? newTags : null)}
                    suggestions={nameSuggestions || []}
                />
            </div>
        );
    }

    return null;
}

function UserProgressSection({ item, users, labels, onSave, saving, nameSuggestions, currentUser, podMemberPicker, coSelectedIds = new Set() }) {
    const showPlayedWith = item.maxPlayers > 2;
    const [expandedUsers, setExpandedUsers] = useState(new Set());

    /** Save a single field for a single user */
    const saveField = (userName, fieldKey, value) => {
        onSave(userName, { [fieldKey]: value });
    };

    const meUser = users.find(u => u.name === currentUser?.name);

    // Co-selected users (played together via PodMemberPicker)
    const coUsers = users.filter(u => u.name !== currentUser?.name && coSelectedIds.has(u.id));

    // Check if a pod member has any activity with this boardgame
    const hasActivity = (userName) => {
        const rating = getUserField(item, userName, 'rating');
        const firstPlayed = getUserField(item, userName, 'firstPlayed');
        const playDate = getUserField(item, userName, 'playDate');
        return !!(rating || firstPlayed || playDate);
    };

    // "From your pods" = others with independent activity who are NOT co-selected
    const othersWithActivity = users.filter(u =>
        u.name !== currentUser?.name && !coSelectedIds.has(u.id) && hasActivity(u.name)
    );

    const toggleExpanded = (userName) => {
        setExpandedUsers((prev) => {
            const next = new Set(prev);
            if (next.has(userName)) next.delete(userName);
            else next.add(userName);
            return next;
        });
    };

    // Build the "You & ..." header
    const togetherLabel = coUsers.length > 0
        ? `You & ${coUsers.map(u => labels[u.name] || u.displayName).join(' & ')}`
        : 'You';
    const togetherEmojis = meUser
        ? [meUser, ...coUsers].map(u => u.emoji).filter(Boolean)
        : [];

    return (
        <div className="space-y-4">
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide">Progress</h4>
            {/* Current user + co-selected together */}
            {meUser && (
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-1.5">
                            {togetherEmojis.map((emoji, i) => (
                                <span key={i} className="text-sm leading-none">{emoji}</span>
                            ))}
                        </div>
                        <span className="text-sm font-medium text-text-primary">{togetherLabel}</span>
                    </div>
                    <div className="space-y-0.5">
                        {BOARDGAME_USER_FIELDS.map((f) => (
                            <InlineField
                                key={f.key}
                                field={f}
                                value={getUserField(item, meUser.name, f.key)}
                                onSave={(fieldKey, val) => saveField(meUser.name, fieldKey, val)}
                                disabled={saving}
                                nameSuggestions={nameSuggestions}
                            />
                        ))}
                        {showPlayedWith && (
                            <PlayedWithRow
                                tags={getUserField(item, meUser.name, 'playedWith') || []}
                                onTagsChange={(val) => saveField(meUser.name, 'playedWith', val)}
                                suggestions={nameSuggestions}
                                podMemberPicker={podMemberPicker}
                                disabled={saving}
                            />
                        )}
                        {!showPlayedWith && podMemberPicker}
                    </div>
                </div>
            )}
            {/* Pod members with independent activity (not co-selected) */}
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide pt-2">From your pods</h4>
            {othersWithActivity.length === 0 ? (
                <p className="text-xs text-text-tertiary italic">No one else in your pods has played this yet</p>
            ) : (
                othersWithActivity.map((u) => {
                    const isExpanded = expandedUsers.has(u.name);
                    return (
                        <div key={u.name} className="space-y-2">
                            <button
                                onClick={() => toggleExpanded(u.name)}
                                className="flex items-center gap-1.5 w-full text-left cursor-pointer"
                            >
                                <span className="text-sm">{u.emoji}</span>
                                <span className="text-sm font-medium text-text-primary">
                                    {labels[u.name] || u.displayName}
                                </span>
                                {!isExpanded && (() => {
                                    const rating = getUserField(item, u.name, 'rating');
                                    const date = formatDateShort(getUserField(item, u.name, 'firstPlayed'));
                                    const parts = [];
                                    if (rating) parts.push('★'.repeat(rating));
                                    if (date) parts.push(date);
                                    return parts.length > 0 ? (
                                        <span className="text-xs text-text-tertiary ml-auto mr-1">{parts.join(' · ')}</span>
                                    ) : null;
                                })()}
                                <svg className={`h-3.5 w-3.5 text-text-tertiary transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {isExpanded && (
                                <div className="space-y-0.5">
                                    {BOARDGAME_USER_FIELDS.map((f) => (
                                        <InlineField
                                            key={f.key}
                                            field={f}
                                            value={getUserField(item, u.name, f.key)}
                                            onSave={(fieldKey, val) => saveField(u.name, fieldKey, val)}
                                            disabled={saving}
                                            nameSuggestions={nameSuggestions}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
}

export default function BoardgameDetailModal({ item, backendService, pocketbaseURL, onClose, onSaved, onDeleted }) {
    const { users: allUsers } = useUsers();
    const { allowedUsers } = usePod();
    const allowed = allowedUsers('boardgames');
    const users = allowed ? allUsers.filter(u => allowed.includes(u.id)) : allUsers;
    const labels = deriveUserLabels(users);
    const { activeView } = useUserView();
    const currentUser = useCurrentUser();
    const [liveItem, setLiveItem] = useState(item);
    const [saving, setSaving] = useState(false);
    const [nameSuggestions, setNameSuggestions] = useState([]);
    const [coSelectedIds, setCoSelectedIds] = useState(new Set());

    const { addToListAction } = useAddToList({
        backendService,
        activeView: currentUser?.name || activeView,
        getItemData: useCallback(() => ({
            itemId: liveItem.id,
            title: liveItem.title,
            imageURL: liveItem.imageURL || '',
        }), [liveItem]),
    });

    useEffect(() => {
        setLiveItem(item);
    }, [item]);

    // Collect all unique playedWith names across all user_boardgames for autocomplete
    useEffect(() => {
        (async () => {
            try {
                const result = await backendService.getAllUserBoardgames();
                const records = result.records ?? [];
                const names = new Set();
                for (const r of records) {
                    if (Array.isArray(r.playedWith)) {
                        for (const n of r.playedWith) names.add(n);
                    }
                }
                setNameSuggestions([...names].sort());
            } catch (_) { /* best effort */ }
        })();
    }, [backendService]);

    const refreshItem = useCallback(async () => {
        const [bgResult, userBgResult] = await Promise.all([
            backendService.getAllBoardgames(),
            backendService.getAllUserBoardgames(),
        ]);
        if (!bgResult.success) return;
        const found = bgResult.boardgames.find((b) => b.id === liveItem.id);
        if (!found) return;

        // Rebuild _userData
        const userBgRecords = userBgResult.records ?? [];
        const userData = {};
        for (const u of users) {
            userData[u.name] = {};
            const userRecord = userBgRecords.find(
                (r) => r.boardgame === found.id && r.user === u.id
            );
            if (userRecord) {
                for (const field of ['firstPlayed', 'rating', 'owned', 'wishlist', 'notes', 'playedWith']) {
                    userData[u.name][field] = userRecord[field] ?? null;
                }
            }
        }
        setLiveItem({ ...found, _userData: userData });
    }, [liveItem.id, users, backendService]);

    // On initial open: if the item arrived without user data (e.g. just added from search),
    // fetch it now so status/rating/etc. are populated.
    useEffect(() => {
        if (!item._userData || Object.keys(item._userData).length === 0) {
            refreshItem();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSaveUser = async (userName, data) => {
        setSaving(true);
        try {
            await backendService.upsertUserBoardgame(userName, liveItem.id, data);
            await refreshItem();
            onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        // Delete user_boardgames join records first (foreign key constraint)
        try {
            const joins = await backendService.db.pb.collection('user_boardgames').getFullList({
                filter: `boardgame="${liveItem.id}"`, $autoCancel: false,
            });
            for (const j of joins) await backendService.proxy.collection('user_boardgames').delete(j.id);
        } catch { /* no join records to delete */ }
        await backendService.deleteBoardgame(liveItem.id);
    };

    // Build image URL
    const pbImage = liveItem.thumbImage;
    const imageURL = pbImage && pocketbaseURL
        ? `${pocketbaseURL}/api/files/boardgames/${liveItem.id}/${pbImage}`
        : liveItem.imageURL ?? null;

    // Build info fields for the header area
    const playerStr = liveItem.minPlayers && liveItem.maxPlayers
        ? liveItem.minPlayers === liveItem.maxPlayers
            ? `${liveItem.minPlayers} players`
            : `${liveItem.minPlayers}-${liveItem.maxPlayers} players`
        : null;

    const timeStr = liveItem.playingTime ? `${liveItem.playingTime} min` : null;
    const bggStr = liveItem.bggRating ? `BGG: ${liveItem.bggRating.toFixed(1)}` : null;

    const metaParts = [playerStr, timeStr, bggStr].filter(Boolean);

    return (
        <DetailModal
            item={liveItem}
            onClose={onClose}
            onSaved={onSaved}
            onDeleted={onDeleted}
            onSave={() => {}}
            onDelete={handleDelete}
            fields={[]}
            editable={false}
            menuActions={[addToListAction]}
            header={{
                imageUrl: imageURL,
                imageAlt: liveItem.title,
                title: liveItem.title,
                subtitle: [
                    liveItem.yearPublished && String(liveItem.yearPublished),
                    liveItem.designer?.length > 0 && liveItem.designer.join(', '),
                ].filter(Boolean).join(' · '),
                tags: [
                    ...(liveItem.playStyle ? [liveItem.playStyle] : []),
                    ...(liveItem.categories?.slice(0, 4) || []),
                ],
                extra: (
                    <div>
                        {metaParts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-text-secondary">
                                {metaParts.map((p, i) => <span key={i}>{p}</span>)}
                            </div>
                        )}
                        {liveItem.bggId && (
                            <a href={`https://boardgamegeek.com/boardgame/${liveItem.bggId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-accent-primary hover:underline">
                                View on BGG
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        )}
                    </div>
                ),
            }}
            extraContent={
                <>
                    <UserProgressSection
                        item={liveItem}
                        users={users}
                        labels={labels}
                        onSave={handleSaveUser}
                        saving={saving}
                        nameSuggestions={nameSuggestions}
                        currentUser={currentUser}
                        coSelectedIds={coSelectedIds}
                        podMemberPicker={
                            <PodMemberPicker
                                itemId={liveItem.id}
                                itemType="boardgame"
                                itemTitle={liveItem.title}
                                activityType="played"
                                label="Played with"
                                hideLabel
                                onChanged={(selectedIds) => {
                                    setCoSelectedIds(selectedIds);
                                    onSaved?.();
                                }}
                            />
                        }
                    />
                </>
            }
        />
    );
}
